import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAsyncEffect } from 'rooks';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { openLinkBasedOnUserPreferences } from '@/core/utilities';
import { ImpactReportDetailModal } from '@/impactReports/ImpactReportDetailModal.page';
import { ImpactReportEditModal } from '@/impactReports/ImpactReportEditModal.page';
import {
  NOMINATE_IMPACT_QUERY_PARAM,
  OPEN_CREATE_MODAL_QUERY_PARAM,
  TRACK_IMPACT,
  VIEW_IMPACT_QUERY_PARAM,
} from '@/impactReports/impactReport.types';
import {
  canEditImpactReport,
  goToWorksheetAndMakeImpactReport,
  nominateWorksheetToBecomeImpactReport,
  removeNomination,
} from '@/impactReports/impactReport.utilities';
import {
  useImpactReportActions,
  useImpactReportDisplayEditModal,
  useImpactReportTableId,
  useImpactReportUpdateNominations,
} from '@/impactReports/impactReports.store';
import { useImpactReportApiActions } from '@/impactReports/useImpactReportApiActions.hook';
import { doTrack } from '@/track/track.service';
import { errorToast } from '@/utilities/toast.utilities';
import { ButtonWithPopover, Icon } from '@seeqdev/qomponents';
import type { FlaggedItem, ImpactReportUseCase } from '@/impactReports/impactReport.types';

type ImpactReportMenuProps = {
  readOnlyMode?: boolean;
};

type ImpactReportMenuAction = {
  iconClass: string;
  action: () => void | Promise<void>;
  translationKey: string;
  disabled: boolean;
};

export const ImpactReportMenu = ({ readOnlyMode = false }: ImpactReportMenuProps) => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [existingReport, setExistingReport] = useState<ImpactReportUseCase | undefined>(undefined);
  const displayEditModal = useImpactReportDisplayEditModal();
  const updateNominations = useImpactReportUpdateNominations();
  const impactReportTableId = useImpactReportTableId();
  const { setDisplayEditModal, setForceTableReload, setUpdateNominations } = useImpactReportActions();
  const { loadImpactReportForWorksheet, getNominatedWorksheets } = useImpactReportApiActions();
  const [displayDetailsModal, setDisplayDetailsModal] = useState(false);
  const [worksheetFlagged, setWorksheetFlagged] = useState(false);
  const [flaggedItems, setFlaggedItems] = useState<FlaggedItem[] | undefined>([]);
  const preferNewTab = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.preferNewTab);
  const currentUser = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser);
  const stateParams = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.stateParams);
  const { workbookId, worksheetId } = stateParams;
  const shouldOpenCreateModal = searchParams.has(OPEN_CREATE_MODAL_QUERY_PARAM);
  const shouldNominateImpactReport = searchParams.has(NOMINATE_IMPACT_QUERY_PARAM);
  const shouldOpenImpactReport = searchParams.has(VIEW_IMPACT_QUERY_PARAM);
  /**
   * This useAsyncEffect listens to URL changes and either flags a worksheet to become an impact report or opens the
   * "create" modal. PAM (the Pricing And Monetization available at usage.seeq.com will trigger the ?nominateImpact
   * query to flag worksheets)
   */
  useAsyncEffect(async () => {
    flaggedItems && setWorksheetFlagged(flaggedItems.some((item) => item.worksheetId === worksheetId));

    if (!readOnlyMode && shouldOpenCreateModal) {
      setDisplayEditModal(true);
    }
    if (!readOnlyMode && shouldNominateImpactReport) {
      await nominateImpactReport(true);
    }
  }, [readOnlyMode, shouldOpenCreateModal, shouldNominateImpactReport, workbookId, worksheetId]);

  /**
   * This useAsyncEffect fetches the list of worksheets that are "nominated" to be turned into impact reports.
   * We use context labels to flag worksheets, and we only show the flagged worksheets that are owned by the current
   * user.
   */
  useAsyncEffect(async () => {
    if (updateNominations && !readOnlyMode) {
      try {
        const nominatedWorksheets = await getNominatedWorksheets();
        setFlaggedItems(nominatedWorksheets);
        // update the worksheetFlagged state if the current worksheet is in the list of flagged worksheets
        setWorksheetFlagged(nominatedWorksheets.some((item) => item.worksheetId === worksheetId));
      } catch (error) {
        errorToast({ httpResponseOrError: error });
      } finally {
        setUpdateNominations(false);
      }
    }
  }, [updateNominations, readOnlyMode]);

  const nominateImpactReport = async (fromQuery = false) => {
    if (!worksheetId || !workbookId) return;

    const doNominate = await nominateWorksheetToBecomeImpactReport(worksheetId, workbookId);
    if (doNominate) {
      setWorksheetFlagged(true);
      setUpdateNominations(true); // it is unlikely that a user flags one of their own worksheets but it's odd not
      // to update the list of flagged worksheets if they do
      doTrack(TRACK_IMPACT, 'Nominate Impact Report', fromQuery ? 'from Query (PAM)' : 'from Toolbar menu');
    } else {
      doTrack(TRACK_IMPACT, 'Nominate Impact Report', 'failed to nominate');
    }
  };

  /**
   * This useAsyncEffect fetches the existing impact report for the current worksheet to make sure the icon (active
   * vs non-active) stays in-sync with the current worksheet
   */
  useAsyncEffect(async () => {
    if (displayEditModal) return;
    setExistingReport(await loadImpactReportForWorksheet(workbookId, worksheetId, impactReportTableId));
  }, [displayEditModal, workbookId, worksheetId, impactReportTableId]);

  useEffect(() => {
    if (readOnlyMode && existingReport && shouldOpenImpactReport) {
      setDisplayDetailsModal(true);
    }
  }, [readOnlyMode, existingReport, shouldOpenImpactReport]);

  const canCreateCurrentWorksheetReport = !readOnlyMode && !existingReport;
  const canOpenCurrentWorksheetReport = Boolean(existingReport) || canCreateCurrentWorksheetReport;
  const canEditExistingReport = existingReport ? canEditImpactReport(existingReport, currentUser.id) : false;
  const showFlaggedItems = !readOnlyMode && flaggedItems && flaggedItems.length > 0;
  const shouldShowEditModal = displayEditModal && (!readOnlyMode || canEditExistingReport);

  const openCurrentWorksheetReport = () => {
    if (!existingReport) {
      if (canCreateCurrentWorksheetReport) {
        setDisplayEditModal(true);
        doTrack(TRACK_IMPACT, 'Create new Impact Report', 'from Toolbar menu');
      }
      return;
    }

    doTrack(TRACK_IMPACT, 'View existing Impact Report', 'from Toolbar menu');
    setDisplayDetailsModal(true);
  };

  const editExistingReport = () => {
    setDisplayDetailsModal(false);
    setDisplayEditModal(true);
  };

  const currentWorksheetReportAction: ImpactReportMenuAction[] = canOpenCurrentWorksheetReport
    ? [
        {
          iconClass: existingReport ? 'fa-eye' : 'fa-pencil',
          action: openCurrentWorksheetReport,
          translationKey:
            existingReport && canEditExistingReport
              ? 'IMPACT_REPORT.VIEW'
              : existingReport
                ? 'IMPACT_REPORT.VIEW_ONLY'
                : 'IMPACT_REPORT.NEW',
          disabled: false,
        },
      ]
    : [];

  const nominateWorksheetAction: ImpactReportMenuAction[] = readOnlyMode
    ? []
    : [
        {
          iconClass: 'fa-star',
          action: async () => await nominateImpactReport(),
          translationKey: 'IMPACT_REPORT.PROMPT_TO_CREATE',
          disabled: worksheetFlagged || Boolean(existingReport) || !pathname.includes('/worksheet'),
        },
      ];

  const actions: ImpactReportMenuAction[] = [
    ...currentWorksheetReportAction,
    {
      iconClass: 'fa-lightbulb-dollar',
      action: () => openLinkBasedOnUserPreferences('/impact', preferNewTab),
      translationKey: 'IMPACT_REPORT.VIEW_ALL',
      disabled: false,
    },
    ...nominateWorksheetAction,
  ];

  return (
    <>
      <div className="icon-container">
        <ButtonWithPopover
          containerTestId="impactReportMenuButtonWithPopover"
          trigger={
            <Icon
              icon="fa-lightbulb-dollar"
              extraClassNames="fa-xlg sq-navbar-default flexNoGrowNoShrink mb1"
              type="white"
              testId="valueCaptureMenu"
              tooltip={t('IMPACT_REPORT.IMPACT_REPORT')}
              tooltipPlacement="bottom"
              iconPrefix={existingReport ? 'fa-solid' : ''}
            />
          }>
          <div className="flexRowContainer sq-text impactReport gap5 tw:mt-sq-10 tw:mb-sq-10">
            {actions.map(({ translationKey, iconClass, action, disabled }) => (
              <div
                data-testid={`impactReportDropdown_${translationKey}`}
                key={`dropdown_${translationKey}`}
                className={`dropdownEntry sq-text-color p5 pl15 pr15 ${disabled ? 'disabled' : ''}`}
                onClick={disabled ? () => {} : action}>
                <Icon icon={iconClass} extraClassNames="fa-fw mr5" type={disabled ? 'gray' : 'text'} />
                {t(translationKey)}
              </div>
            ))}
            {showFlaggedItems ? (
              <>
                <div className="popover-header">
                  <Icon icon="fa-star" extraClassNames="pr5" type="text" />
                  {t('IMPACT_REPORT.FLAGGED_WORKSHEETS')}
                </div>

                <div className="flexRowContainer gap5 tw:max-w-sq-300! tw:pt-sq-5 tw:pr-sq-15 tw:pb-sq-5 tw:pl-sq-15">
                  {flaggedItems.map(({ name, workbookId, worksheetId, labelId }) => (
                    <div className="flexColumnContainer tw:items-center" key={`dropdown_${name}`}>
                      <div
                        className="link cursorPointer fakeLink flexFill tw:pr-sq-5"
                        onClick={() => {
                          workbookId && goToWorksheetAndMakeImpactReport(worksheetId, workbookId, preferNewTab);
                        }}>
                        {name}
                      </div>
                      <Icon
                        icon="fa-close"
                        extraClassNames="pl5 cursorPointer"
                        tooltip={t('IMPACT_REPORT.REMOVE_NOMINATION')}
                        onClick={async () => {
                          const removed = labelId ? await removeNomination(labelId, worksheetId) : false;
                          if (removed) {
                            setUpdateNominations(true);
                          } else {
                            errorToast({ messageKey: 'IMPACT_REPORT.REMOVE_NOMINATION_ERROR' });
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </ButtonWithPopover>
        {displayDetailsModal && existingReport ? (
          <ImpactReportDetailModal
            closeFn={() => setDisplayDetailsModal(false)}
            details={existingReport}
            editCallbackFn={canEditExistingReport ? editExistingReport : undefined}
          />
        ) : null}
        {shouldShowEditModal ? (
          <ImpactReportEditModal
            existingReport={existingReport}
            closeFn={(reload = false) => {
              if (reload && window.location.pathname === '/impact') {
                setForceTableReload(true);
              }
              setDisplayEditModal(false);
            }}
          />
        ) : null}
        {showFlaggedItems ? (
          <span className="icon-badge" data-testid="notificationCount" id="notification-count">
            {flaggedItems.length}
          </span>
        ) : null}
      </div>
    </>
  );
};
